// api.ts — chiamate all'API con tempo massimo, errori leggibili e nessun testo nel codice:
// l'errore porta un codice, la schermata lo traduce.
import { BASE_API, TIMEOUT_RICHIESTA_PREDEFINITO_SECONDI } from "../costanti.ts";

// Il tempo massimo arriva dalle impostazioni (app.richiesta_timeout_secondi) quando sono state lette.
let timeoutMs = TIMEOUT_RICHIESTA_PREDEFINITO_SECONDI * 1000;
export function impostaTimeout(secondi: number): void {
  if (Number.isFinite(secondi) && secondi > 0) timeoutMs = secondi * 1000;
}

export type CodiceErroreRete = "offline" | "timeout" | "server" | "manutenzione" | string;

export class ErroreRete extends Error {
  readonly codice: CodiceErroreRete;
  readonly stato: number;
  readonly dettagli: Record<string, unknown> | undefined;
  constructor(codice: CodiceErroreRete, stato = 0, dettagli?: Record<string, unknown>) {
    super(codice);
    this.codice = codice;
    this.stato = stato;
    this.dettagli = dettagli;
  }
}

export interface OpzioniRichiesta {
  metodo?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  corpo?: unknown;
  intestazioni?: Record<string, string>;
  credenziali?: RequestCredentials;
}

// Sessione dell'utente registrato: la fornisce servizi/account.ts (qui niente import, per non creare cicli).
let sessione: () => string | null = () => null;
let connesso: () => boolean = () => false;
/** `token`: sessione delle app native (Authorization); `attiva`: c'è una sessione (anche solo il cookie del sito). */
export function impostaSessione(token: () => string | null, attiva: () => boolean): void {
  sessione = token;
  connesso = attiva;
}
/** 401 su una rotta con la sessione: la sessione non vale più (scaduta, revocata, account cancellato). */
let suSessioneScaduta: () => void = () => undefined;
export function impostaSuSessioneScaduta(f: () => void): void {
  suSessioneScaduta = f;
}

export async function richiesta<T>(percorso: string, opz: OpzioniRichiesta = {}): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) throw new ErroreRete("offline");
  const controllo = new AbortController();
  const timer = setTimeout(() => controllo.abort(), timeoutMs);
  let risposta: Response;
  // La sessione va solo all'API dell'app (mai al pannello, che ha il suo cookie).
  const app = percorso.startsWith("/api/v1/") && !percorso.startsWith("/api/v1/admin");
  const token = app ? sessione() : null;
  const conSessione = app && (token !== null || connesso());
  const metodo = opz.metodo ?? "GET";
  try {
    risposta = await fetch(`${BASE_API}${percorso}`, {
      method: metodo,
      headers: {
        accept: "application/json",
        // Il cookie della sessione vale per ciò che cambia qualcosa solo con questa intestazione (un altro sito non
        // può aggiungerla: difesa contro le richieste falsificate).
        ...(app && metodo !== "GET" ? { "x-laughdown-richiesta": "1" } : {}),
        ...(opz.corpo !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...opz.intestazioni,
      },
      body: opz.corpo !== undefined ? JSON.stringify(opz.corpo) : undefined,
      credentials: opz.credenziali ?? "same-origin",
      signal: controllo.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw new ErroreRete("timeout");
    throw new ErroreRete(navigator.onLine === false ? "offline" : "server");
  } finally {
    clearTimeout(timer);
  }
  if (risposta.status === 204) return undefined as T;
  let dati: unknown = null;
  try {
    dati = await risposta.json();
  } catch {
    dati = null;
  }
  if (!risposta.ok) {
    const d = (dati ?? {}) as { errore?: string; dettagli?: Record<string, unknown> };
    if (risposta.status === 401 && conSessione && d.errore === "non_autenticato") suSessioneScaduta();
    throw new ErroreRete(d.errore ?? "server", risposta.status, d.dettagli);
  }
  return dati as T;
}

/** Scarica un file dall'API (es. il PDF di una liberatoria) con la sessione e lo fa salvare con questo nome. */
export async function scaricaFile(percorso: string, nomeFile: string): Promise<void> {
  const token = percorso.startsWith("/api/v1/") && !percorso.startsWith("/api/v1/admin") ? sessione() : null;
  let r: Response;
  try {
    r = await fetch(`${BASE_API}${percorso}`, { headers: token ? { authorization: `Bearer ${token}` } : {}, credentials: "same-origin" });
  } catch {
    throw new ErroreRete(navigator.onLine === false ? "offline" : "server");
  }
  if (!r.ok) {
    if (r.status === 401 && (token !== null || connesso())) suSessioneScaduta();
    throw new ErroreRete("server", r.status);
  }
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** v037: manda dati binari (un pezzo di video, un'immagine) con la sessione, come `richiesta`. Risposta JSON. */
export async function mandaBinario<T>(percorso: string, dati: Blob, tipo: string, intestazioni: Record<string, string> = {}, metodo: "PUT" | "POST" = "PUT"): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) throw new ErroreRete("offline");
  const token = sessione();
  const controllo = new AbortController();
  const timer = setTimeout(() => controllo.abort(), Math.max(timeoutMs, 60_000));
  let r: Response;
  try {
    r = await fetch(`${BASE_API}${percorso}`, {
      method: metodo,
      headers: { accept: "application/json", "content-type": tipo, "x-laughdown-richiesta": "1", ...(token ? { authorization: `Bearer ${token}` } : {}), ...intestazioni },
      body: dati,
      credentials: "same-origin",
      signal: controllo.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw new ErroreRete("timeout");
    throw new ErroreRete(navigator.onLine === false ? "offline" : "server");
  } finally {
    clearTimeout(timer);
  }
  let d: unknown = null;
  try {
    d = await r.json();
  } catch {
    d = null;
  }
  if (!r.ok) throw new ErroreRete(((d ?? {}) as { errore?: string }).errore ?? "server", r.status);
  return d as T;
}
